// Recruitment — the 3-stage hiring lifecycle inside the HR module.
//   Openings  : Stage 1 job requisitions (HR raises, management approves).
//   Pipeline  : Stage 2 ATS kanban (drag candidates across rounds).
//   Onboarding: Stage 3 hire → auto-creates an employee + joining documents.
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { HRC, money, fmtDate, Card, SectionTitle, SubTabs, Th, Td, Table, Pill, Btn, Field, input, Modal, Empty, Loading } from './hrkit';
import { CandidateLetters } from './Letters';
import { reportError } from '../../utils/reportError';

// Public-facing prod origin for the candidate application link (never localhost).
const SHARE_BASE = 'https://erp.destinytextile.com';

const STAGES = [
  { key: 'applied', label: 'Applied', color: HRC.navy },
  { key: 'round_1', label: 'Round 1', color: '#8B5CF6' },
  { key: 'round_2', label: 'Round 2', color: '#8B5CF6' },
  { key: 'round_3', label: 'Round 3', color: '#8B5CF6' },
  { key: 'hired', label: 'Hired', color: HRC.green },
  { key: 'rejected', label: 'Rejected', color: HRC.red },
];
const roundNum = (stage) => (stage && stage.startsWith('round_') ? Number(stage.split('_')[1]) : 0);

export default function Recruitment({ companyCode, ...props }) {
  const [view, setView] = useState('pipeline');
  return (
    <div>
      {companyCode && <ShareLink companyCode={companyCode} />}
      <SubTabs
        tabs={[{ key: 'pipeline', label: '🧑‍💼 Candidate Pipeline' }, { key: 'openings', label: '📋 Job Openings' }, { key: 'grading', label: '📝 Test Grading' }]}
        active={view} onChange={setView}
      />
      {view === 'pipeline' ? <Pipeline {...props} /> : view === 'openings' ? <Openings {...props} /> : <GradingQueue />}
    </div>
  );
}

// Shareable public application link for the active company (rsr|deroi). HR pastes
// this into email/WhatsApp; candidates apply with no login.
function ShareLink({ companyCode }) {
  const link = `${SHARE_BASE}/apply/${String(companyCode).toLowerCase()}`;
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); }
    catch {
      const ta = document.createElement('textarea');
      ta.value = link; document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); } catch (e) {}
      ta.remove();
    }
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };
  return (
    <Card style={{ marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', background: HRC.violet, border: `1px solid ${HRC.border}` }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: HRC.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>Public Application Link · {companyCode}</div>
        <a href={link} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: HRC.navy, fontWeight: 700, marginTop: 3, display: 'inline-block', fontFamily: 'monospace', wordBreak: 'break-all' }}>{link}</a>
        <div style={{ fontSize: 11, color: HRC.muted, marginTop: 2 }}>Share with candidates via email / WhatsApp — they apply with no login.</div>
      </div>
      <Btn onClick={copy}>{copied ? '✓ Copied!' : '📋 Copy Application Link'}</Btn>
    </Card>
  );
}

/* ═══════════════════════ STAGE 1 — OPENINGS ═══════════════════════ */
function Openings({ onChange }) {
  const { user } = useAuth();
  const canApprove = ['admin', 'management'].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [edit, setEdit] = useState(null);
  const [rubricFor, setRubricFor] = useState(null);
  const [compareFor, setCompareFor] = useState(null);
  const [questionsFor, setQuestionsFor] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/hr/recruitment/requisitions')
      .then(r => setRows(r.data.data || [])).catch(() => setRows([])).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  const refresh = () => { load(); onChange?.(); };

  const approve = (id) => api.post(`/hr/recruitment/requisitions/${id}/approve`).then(refresh).catch(reportError('approve this requisition'));
  const close = (id) => api.post(`/hr/recruitment/requisitions/${id}/close`).then(refresh).catch(reportError('close this requisition'));

  return (
    <div>
      <Card>
        <SectionTitle right={<Btn onClick={() => { setEdit(null); setShowForm(true); }}>+ Raise Requisition</Btn>}>Job Requisitions</SectionTitle>
        {loading ? <Loading /> : rows.length === 0 ? <Empty>No openings raised yet.</Empty> : (
          <Table>
            <thead>
              <tr>
                <Th>Position</Th><Th>Vacancy</Th><Th>Urgency</Th><Th>Expected By</Th>
                <Th>Candidates</Th><Th>Status</Th><Th>Approver</Th><Th style={{ textAlign: 'right' }}></Th>
              </tr>
            </thead>
            <tbody>
              {rows.map(rq => (
                <tr key={rq.id}>
                  <Td>
                    <div style={{ fontWeight: 600 }}>{rq.job_title}</div>
                    {rq.qualification && <div style={{ fontSize: 11, color: HRC.muted }}>{rq.qualification}</div>}
                  </Td>
                  <Td>{rq.total_vacancy}</Td>
                  <Td><span style={{ color: rq.urgency === 'urgent' ? HRC.red : HRC.muted, fontWeight: 700, fontSize: 12, textTransform: 'capitalize' }}>{rq.urgency}</span></Td>
                  <Td style={{ whiteSpace: 'nowrap' }}>{rq.expected_hiring_date ? fmtDate(rq.expected_hiring_date) : '—'}</Td>
                  <Td>{rq.candidate_count || 0}</Td>
                  <Td><Pill value={rq.status} /></Td>
                  <Td style={{ fontSize: 12 }}>{rq.approved_by_name || '—'}</Td>
                  <Td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {rq.status === 'pending_approval' && canApprove && <Btn size="sm" onClick={() => approve(rq.id)}>Approve</Btn>}
                    <Btn size="sm" variant="soft" onClick={() => setRubricFor(rq)} style={{ marginLeft: 6 }}>Rubric</Btn>
                    <Btn size="sm" variant="soft" onClick={() => setQuestionsFor(rq)} style={{ marginLeft: 6 }}>Questions</Btn>
                    <Btn size="sm" variant="soft" onClick={() => setCompareFor(rq)} style={{ marginLeft: 6 }}>Compare</Btn>
                    <Btn size="sm" variant="soft" onClick={() => { setEdit(rq); setShowForm(true); }} style={{ marginLeft: 6 }}>Edit</Btn>
                    {rq.status !== 'closed' && rq.status !== 'filled' && <Btn size="sm" variant="ghost" onClick={() => close(rq.id)} style={{ marginLeft: 6 }}>Close</Btn>}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
      {showForm && <RequisitionForm rq={edit} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); refresh(); }} />}
      {rubricFor && <RubricEditor requisition={rubricFor} onClose={() => setRubricFor(null)} />}
      {questionsFor && <QuestionsEditor requisition={questionsFor} onClose={() => setQuestionsFor(null)} />}
      {compareFor && <CompareMatrix requisition={compareFor} onClose={() => setCompareFor(null)} />}
    </div>
  );
}

function RequisitionForm({ rq, onClose, onSaved }) {
  const init = {
    job_title: '', roles_responsibility: '', total_vacancy: 1, urgency: 'normal', expected_hiring_date: '',
    gender_preferred: 'any', salary_range: '', marital_status_pref: 'any', experience_required: '',
    qualification: '', special_skills: '', age_range: '', job_timing: '', working_tools: '',
    conveyance_required: false, others_notes: '', form_filler: '', test_duration_mins: 30,
  };
  const [f, setF] = useState(rq ? { ...init, ...clean(rq) } : init);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (k) => (e) => setF(p => ({ ...p, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const submit = () => {
    if (!f.job_title.trim()) { setErr('Job title is required'); return; }
    setSaving(true); setErr('');
    const req = rq ? api.patch(`/hr/recruitment/requisitions/${rq.id}`, f) : api.post('/hr/recruitment/requisitions', f);
    req.then(() => onSaved()).catch(e => setErr(e.response?.data?.error || 'Failed to save')).finally(() => setSaving(false));
  };

  return (
    <Modal title={rq ? 'Edit Requisition' : 'Raise Job Requisition'} onClose={onClose} width={720}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Job Title *"><input style={input} value={f.job_title} onChange={set('job_title')} /></Field>
        <Field label="Total Vacancy"><input type="number" min={1} style={input} value={f.total_vacancy} onChange={set('total_vacancy')} /></Field>
        <div style={{ gridColumn: '1/-1' }}>
          <Field label="Roles & Responsibility"><textarea style={{ ...input, height: 60, resize: 'vertical' }} value={f.roles_responsibility} onChange={set('roles_responsibility')} /></Field>
        </div>
        <Field label="Urgency">
          <select style={input} value={f.urgency} onChange={set('urgency')}><option value="normal">Normal</option><option value="urgent">Urgent</option></select>
        </Field>
        <Field label="Expected Hiring Date"><input type="date" style={input} value={dval(f.expected_hiring_date)} onChange={set('expected_hiring_date')} /></Field>
        <Field label="Gender Preferred">
          <select style={input} value={f.gender_preferred} onChange={set('gender_preferred')}><option value="any">Any</option><option value="male">Male</option><option value="female">Female</option></select>
        </Field>
        <Field label="Marital Status Pref">
          <select style={input} value={f.marital_status_pref} onChange={set('marital_status_pref')}><option value="any">Any</option><option value="single">Single</option><option value="married">Married</option></select>
        </Field>
        <Field label="Salary Range"><input style={input} value={f.salary_range} onChange={set('salary_range')} placeholder="e.g. 15,000–20,000/mo" /></Field>
        <Field label="Experience Required"><input style={input} value={f.experience_required} onChange={set('experience_required')} placeholder="e.g. 2–3 years" /></Field>
        <Field label="Qualification"><input style={input} value={f.qualification} onChange={set('qualification')} /></Field>
        <Field label="Age Range"><input style={input} value={f.age_range} onChange={set('age_range')} placeholder="e.g. 21–35" /></Field>
        <Field label="Job Timing"><input style={input} value={f.job_timing} onChange={set('job_timing')} placeholder="e.g. 9:30–18:00" /></Field>
        <Field label="Test Duration (min)"><input type="number" min={5} style={input} value={f.test_duration_mins} onChange={set('test_duration_mins')} /></Field>
        <Field label="Form Filler (HR)"><input style={input} value={f.form_filler} onChange={set('form_filler')} placeholder="defaults to you" /></Field>
        <div style={{ gridColumn: '1/-1' }}>
          <Field label="Special Skills"><input style={input} value={f.special_skills} onChange={set('special_skills')} /></Field>
        </div>
        <div style={{ gridColumn: '1/-1' }}>
          <Field label="Working Tools"><input style={input} value={f.working_tools} onChange={set('working_tools')} placeholder="e.g. flatlock machine, overlock" /></Field>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginTop: 6 }}>
          <input type="checkbox" checked={!!f.conveyance_required} onChange={set('conveyance_required')} /> Conveyance required
        </label>
        <div style={{ gridColumn: '1/-1' }}>
          <Field label="Other Notes"><textarea style={{ ...input, height: 50, resize: 'vertical' }} value={f.others_notes} onChange={set('others_notes')} /></Field>
        </div>
      </div>
      {err && <div style={{ color: HRC.red, fontSize: 12, marginTop: 12 }}>{err}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn onClick={submit} disabled={saving}>{saving ? 'Saving…' : rq ? 'Save Changes' : 'Submit for Approval'}</Btn>
      </div>
    </Modal>
  );
}

/* ═══════════════════════ STAGE 2 — PIPELINE (KANBAN) ═══════════════════════ */
function Pipeline({ onChange, openChecklist }) {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reqs, setReqs] = useState([]);
  const [reqFilter, setReqFilter] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [moveTo, setMoveTo] = useState(null);  // { card, stage } → round-notes / reject / hire flow

  const load = useCallback(() => {
    setLoading(true);
    api.get('/hr/recruitment/candidates', { params: reqFilter ? { requisition_id: reqFilter } : {} })
      .then(r => setCards(r.data.data || [])).catch(() => setCards([])).finally(() => setLoading(false));
  }, [reqFilter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/hr/recruitment/requisitions', { params: { status: 'approved' } }).then(r => setReqs(r.data.data || [])).catch(() => {}); }, []);
  const refresh = () => { load(); onChange?.(); };

  const handleDrop = (stage) => {
    const card = cards.find(c => c.id === dragId);
    setDragId(null);
    if (!card || card.stage === stage) return;
    if (stage === 'applied') {
      api.patch(`/hr/recruitment/candidates/${card.id}`, { stage }).then(refresh).catch(reportError('move this candidate'));
      return;
    }
    setMoveTo({ card, stage });   // rounds / rejected / hired need extra input
  };

  return (
    <div>
      <Card style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 12, color: HRC.muted }}>Drag a card across columns to advance a candidate.</span>
            <select value={reqFilter} onChange={e => setReqFilter(e.target.value)} style={{ ...input, width: 'auto' }}>
              <option value="">All openings</option>
              {reqs.map(r => <option key={r.id} value={r.id}>{r.job_title}</option>)}
            </select>
          </div>
          <Btn onClick={() => setShowAdd(true)}>+ Add Candidate</Btn>
        </div>
      </Card>

      {loading ? <Loading /> : (
        <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 8 }}>
          {STAGES.map(col => {
            const items = cards.filter(c => c.stage === col.key);
            return (
              <div key={col.key}
                onDragOver={e => e.preventDefault()}
                onDrop={e => { e.preventDefault(); handleDrop(col.key); }}
                style={{ flex: '0 0 220px', background: HRC.pageBg, borderRadius: 10, border: `1px solid ${HRC.border}`, padding: 8, minHeight: 120 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 6px 8px' }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: col.color }}>{col.label}</span>
                  <span style={{ fontSize: 11, color: HRC.muted, background: '#fff', borderRadius: 10, padding: '0 7px' }}>{items.length}</span>
                </div>
                {/* setData is required by Firefox to begin a drag at all — without it the card
                    simply never lifts, with no error. The payload stays in state. */}
                {items.map(c => (
                  <div key={c.id} draggable
                    onDragStart={e => { e.dataTransfer.setData('text/plain', c.id); e.dataTransfer.effectAllowed = 'move'; setDragId(c.id); }}
                    onClick={() => setDetailId(c.id)}
                    style={{ background: '#fff', borderRadius: 8, border: `1px solid ${HRC.border}`, borderLeft: `3px solid ${col.color}`, padding: '8px 10px', marginBottom: 6, cursor: 'pointer' }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{c.candidate_name}</div>
                    <div style={{ fontSize: 11, color: HRC.muted, marginTop: 2 }}>{c.designation_applied || '—'}</div>
                    <div style={{ fontSize: 10, color: HRC.muted, marginTop: 4, display: 'flex', justifyContent: 'space-between' }}>
                      <span>{c.reference_source || c.source_channel}</span>
                      {c.employee_code && <span style={{ color: HRC.green, fontWeight: 700 }}>{c.employee_code}</span>}
                    </div>
                  </div>
                ))}
                {items.length === 0 && <div style={{ textAlign: 'center', color: '#bbb', fontSize: 11, padding: '14px 0' }}>—</div>}
              </div>
            );
          })}
        </div>
      )}

      {showAdd && <AddCandidate reqs={reqs} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); refresh(); }} />}
      {detailId && <CandidateDetail id={detailId} requisitions={reqs} onClose={() => setDetailId(null)} onChanged={refresh}
        onReject={(card) => { setDetailId(null); setMoveTo({ card, stage: 'rejected' }); }}
        onHire={(card) => { setDetailId(null); setMoveTo({ card, stage: 'hired' }); }} />}

      {moveTo && moveTo.stage === 'rejected' && (
        <RejectModal card={moveTo.card} onClose={() => setMoveTo(null)} onDone={() => { setMoveTo(null); refresh(); }} />
      )}
      {moveTo && roundNum(moveTo.stage) > 0 && (
        <RoundModal card={moveTo.card} stage={moveTo.stage} onClose={() => setMoveTo(null)} onDone={() => { setMoveTo(null); refresh(); }} />
      )}
      {moveTo && moveTo.stage === 'hired' && (
        <OnboardingModal card={moveTo.card} onClose={() => setMoveTo(null)} onDone={() => { setMoveTo(null); refresh(); }} onOpenChecklist={openChecklist} />
      )}
    </div>
  );
}

function AddCandidate({ reqs, onClose, onSaved }) {
  const [f, setF] = useState({ candidate_name: '', contact_no: '', email_id: '', designation_applied: '', marital_status: '', reference_source: '', contact_person: '', requisition_id: '', cv_url: '' });
  const [saving, setSaving] = useState(false); const [err, setErr] = useState('');
  const set = (k) => (e) => setF(p => ({ ...p, [k]: e.target.value }));
  const submit = () => {
    if (!f.candidate_name.trim()) { setErr('Candidate name is required'); return; }
    setSaving(true); setErr('');
    api.post('/hr/recruitment/candidates', f).then(onSaved).catch(e => setErr(e.response?.data?.error || 'Failed')).finally(() => setSaving(false));
  };
  return (
    <Modal title="Add Candidate" onClose={onClose} width={620}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Candidate Name *"><input style={input} value={f.candidate_name} onChange={set('candidate_name')} /></Field>
        <Field label="Post Applied For"><input style={input} value={f.designation_applied} onChange={set('designation_applied')} /></Field>
        <Field label="Contact No"><input style={input} value={f.contact_no} onChange={set('contact_no')} /></Field>
        <Field label="Email"><input style={input} value={f.email_id} onChange={set('email_id')} /></Field>
        <Field label="Marital Status">
          <select style={input} value={f.marital_status} onChange={set('marital_status')}><option value="">—</option><option value="single">Single</option><option value="married">Married</option><option value="other">Other</option></select>
        </Field>
        <Field label="Reference / Source"><input style={input} value={f.reference_source} onChange={set('reference_source')} placeholder="naukri / referral / walk_in" /></Field>
        <Field label="Contact Person (internal)"><input style={input} value={f.contact_person} onChange={set('contact_person')} /></Field>
        <Field label="Against Opening">
          <select style={input} value={f.requisition_id} onChange={set('requisition_id')}>
            {reqs.map(r => <option key={r.id} value={r.id}>{r.job_title} · {r.total_vacancy} vacanc{r.total_vacancy > 1 ? 'ies' : 'y'}</option>)}
            <option value="">Other / speculative (not testable)</option>
          </select>
        </Field>
        <div style={{ gridColumn: '1/-1' }}>
          <Field label="CV Link (optional)"><input style={input} value={f.cv_url} onChange={set('cv_url')} placeholder="https://…" /></Field>
        </div>
      </div>
      {err && <div style={{ color: HRC.red, fontSize: 12, marginTop: 12 }}>{err}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn onClick={submit} disabled={saving}>{saving ? 'Saving…' : 'Add Candidate'}</Btn>
      </div>
    </Modal>
  );
}

function CandidateDetail({ id, requisitions = [], onClose, onChanged, onReject, onHire }) {
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => { setLoading(true); api.get(`/hr/recruitment/candidates/${id}`).then(r => setC(r.data.data)).catch(() => setC(null)).finally(() => setLoading(false)); }, [id]);
  useEffect(() => { load(); }, [load]);

  const stageLabel = (STAGES.find(s => s.key === c?.stage) || {}).label || c?.stage;
  const cvHref = c?.cv_url ? (c.cv_url.startsWith('http') ? c.cv_url : c.cv_url) : null;

  return (
    <Modal title="Candidate" onClose={onClose} width={620}>
      {loading ? <Loading /> : !c ? <Empty>Could not load.</Empty> : (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 800 }}>{c.candidate_name}</div>
              <div style={{ fontSize: 12, color: HRC.muted, marginTop: 2 }}>{c.designation_applied || '—'}{c.requisition_title ? ` · ${c.requisition_title}` : ''}</div>
            </div>
            <Pill value={c.stage} label={stageLabel} />
          </div>

          <VacancyPicker candidate={c} requisitions={requisitions} onChanged={load} />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
            <ReadField label="Contact" value={c.contact_no} />
            <ReadField label="Email" value={c.email_id} />
            <ReadField label="Marital Status" value={c.marital_status} />
            <ReadField label="Source" value={c.reference_source || c.source_channel} />
            <ReadField label="Contact Person" value={c.contact_person} />
            <ReadField label="Applied" value={c.applied_at ? fmtDate(c.applied_at) : '—'} />
          </div>

          {cvHref ? (
            <a href={cvHref} target="_blank" rel="noreferrer"><Btn size="sm" variant="soft">📎 View CV</Btn></a>
          ) : <span style={{ fontSize: 12, color: HRC.muted }}>No CV on file</span>}

          <div style={{ marginTop: 16 }}>
            <SectionTitle>Round History</SectionTitle>
            {[1, 2, 3].map(n => {
              const st = c[`round_${n}_status`], dt = c[`round_${n}_date`], nt = c[`round_${n}_notes`];
              if (!st && !dt && !nt) return null;
              return (
                <div key={n} style={{ borderLeft: `3px solid #8B5CF6`, paddingLeft: 10, marginBottom: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 700 }}>Round {n} {st ? <span style={{ textTransform: 'capitalize', color: HRC.muted }}>· {st.replace(/_/g, ' ')}</span> : ''} {dt ? <span style={{ color: HRC.muted, fontWeight: 400 }}>· {fmtDate(dt)}</span> : ''}</div>
                  {nt && <div style={{ fontSize: 12, color: HRC.text, marginTop: 2 }}>{nt}</div>}
                </div>
              );
            })}
            {![1, 2, 3].some(n => c[`round_${n}_status`] || c[`round_${n}_notes`]) && <div style={{ fontSize: 12, color: HRC.muted }}>No rounds recorded yet.</div>}
          </div>

          {c.stage === 'rejected' && c.rejected_reason && (
            <div style={{ marginTop: 12, background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, fontSize: 12 }}>Rejected: {c.rejected_reason}</div>
          )}
          {c.stage === 'hired' && c.employee_code && (
            <div style={{ marginTop: 12, background: '#E7F6EF', color: HRC.green, padding: '8px 12px', borderRadius: 6, fontSize: 12, fontWeight: 700 }}>✓ Hired as employee {c.employee_code}</div>
          )}

          <CandidateScorecard candidateId={c.id} />

          <CandidateLetters candidateId={c.id} />

          {c.stage !== 'hired' && c.stage !== 'rejected' && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
              <Btn variant="danger" onClick={() => onReject(c)}>Reject</Btn>
              <Btn onClick={() => onHire(c)}>✓ Mark Hired</Btn>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

function RoundModal({ card, stage, onClose, onDone }) {
  const n = roundNum(stage);
  const [f, setF] = useState({ status: 'pending', date: '', notes: '' });
  const [saving, setSaving] = useState(false); const [err, setErr] = useState('');
  const set = (k) => (e) => setF(p => ({ ...p, [k]: e.target.value }));
  const submit = () => {
    setSaving(true); setErr('');
    api.patch(`/hr/recruitment/candidates/${card.id}`, {
      stage, [`round_${n}_status`]: f.status, [`round_${n}_date`]: f.date, [`round_${n}_notes`]: f.notes,
    }).then(onDone).catch(e => setErr(e.response?.data?.error || 'Failed')).finally(() => setSaving(false));
  };
  return (
    <Modal title={`Round ${n} — ${card.candidate_name}`} onClose={onClose} width={460}>
      <div style={{ display: 'grid', gap: 12 }}>
        <Field label="Outcome">
          <select style={input} value={f.status} onChange={set('status')}>
            <option value="pending">Pending</option><option value="cleared">Cleared</option>
            <option value="on_hold">On Hold</option><option value="rejected">Rejected</option>
          </select>
        </Field>
        <Field label="Interview Date"><input type="date" style={input} value={f.date} onChange={set('date')} /></Field>
        <Field label="Notes"><textarea style={{ ...input, height: 70, resize: 'vertical' }} value={f.notes} onChange={set('notes')} /></Field>
      </div>
      {err && <div style={{ color: HRC.red, fontSize: 12, marginTop: 10 }}>{err}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn onClick={submit} disabled={saving}>{saving ? 'Saving…' : `Move to Round ${n}`}</Btn>
      </div>
    </Modal>
  );
}

function RejectModal({ card, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = () => { setSaving(true); api.post(`/hr/recruitment/candidates/${card.id}/reject`, { reason }).then(onDone).catch(() => setSaving(false)); };
  return (
    <Modal title={`Reject — ${card.candidate_name}`} onClose={onClose} width={440}>
      <Field label="Reason"><textarea style={{ ...input, height: 80, resize: 'vertical' }} value={reason} onChange={e => setReason(e.target.value)} placeholder="Why is this candidate being rejected?" /></Field>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn variant="danger" onClick={submit} disabled={saving}>{saving ? 'Saving…' : 'Reject Candidate'}</Btn>
      </div>
    </Modal>
  );
}

/* ═══════════════════════ STAGE 3 — ONBOARDING (HIRE) ═══════════════════════ */
const DOC_SLOTS = [
  { key: 'photo', label: 'Passport Photo' }, { key: 'aadhaar', label: 'Aadhaar Card' },
  { key: 'pan', label: 'PAN Card' }, { key: 'bank_proof', label: 'Bank Proof (cheque/passbook)' },
  { key: 'education', label: 'Education Certificate' }, { key: 'experience', label: 'Experience Letter' },
];

function OnboardingModal({ card, onClose, onDone, onOpenChecklist }) {
  const [result, setResult] = useState(null);   // set on successful hire → success panel
  const [f, setF] = useState({
    full_name: card.candidate_name || '', father_name: '', mother_name: '', gender: '',
    date_of_birth: '', phone_number: card.contact_no || '', email_id: card.email_id || '',
    correspondence_address: '', permanent_address: '', education: '', experience: '',
    aadhaar_number: '', marital_status: card.marital_status || '', category: '', emergency_number: '',
    department_id: '', designation_id: '', employee_type: 'monthly', salary: '',
    date_of_joining: '', shift_start: '09:30', shift_end: '18:00',
    // statutory + family (mig 063)
    esi_no: '', pf_no: '', uan: '', spouse_name: '', spouse_dob: '', emergency_contact_relation: '',
  });
  const [children, setChildren] = useState([]);   // [{ relation, name, dob }]
  const spouseLabel = f.gender === 'female' ? 'Husband Name' : f.gender === 'male' ? 'Wife Name' : 'Spouse Name';
  const [depts, setDepts] = useState([]);
  const [desigs, setDesigs] = useState([]);
  const [files, setFiles] = useState({});
  const [saving, setSaving] = useState(false); const [err, setErr] = useState('');
  const set = (k) => (e) => setF(p => ({ ...p, [k]: e.target.value }));

  useEffect(() => { api.get('/hr/departments').then(r => setDepts(r.data.data || [])).catch(() => {}); }, []);
  useEffect(() => {
    if (!f.department_id) { setDesigs([]); return; }
    api.get('/hr/designations', { params: { department_id: f.department_id } }).then(r => setDesigs(r.data.data || [])).catch(() => setDesigs([]));
  }, [f.department_id]);

  const submit = () => {
    if (!f.full_name.trim()) { setErr('Name is required'); return; }
    setSaving(true); setErr('');
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => { if (v != null && v !== '') fd.append(k, v); });
    const kids = children.filter(c => c.name && c.name.trim());
    if (kids.length) fd.append('children', JSON.stringify(kids));
    DOC_SLOTS.forEach(d => { if (files[d.key]) fd.append(d.key, files[d.key]); });
    api.post(`/hr/recruitment/candidates/${card.id}/hire`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then(r => setResult(r.data?.data || {})).catch(e => setErr(e.response?.data?.error || 'Failed to hire')).finally(() => setSaving(false));
  };

  // Success state — employee created + 30-item joining checklist auto-generated.
  if (result) {
    return (
      <Modal title="Hired ✓" onClose={onDone} width={520}>
        <div style={{ textAlign: 'center', padding: '8px 0 4px' }}>
          <div style={{ fontSize: 48 }}>🎉</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: HRC.text, marginTop: 6 }}>{f.full_name} hired</div>
          {result.employee_code && <div style={{ fontSize: 13, color: HRC.green, fontWeight: 700, marginTop: 4 }}>Employee {result.employee_code}</div>}
          <div style={{ fontSize: 12, color: HRC.muted, marginTop: 8 }}>A 30-item joining checklist has been created for this hire.</div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20 }}>
            <Btn variant="ghost" onClick={onDone}>Done</Btn>
            {result.id && onOpenChecklist && (
              <Btn onClick={() => onOpenChecklist(result.id)}>📋 Open Joining Checklist</Btn>
            )}
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={`Onboard — ${card.candidate_name}`} onClose={onClose} width={780}>
      <div style={{ fontSize: 12, color: HRC.muted, marginBottom: 12 }}>Completing this creates an active employee record and a new employee code. The candidate stays in history, linked to the new employee.</div>

      <SectionTitle>Personal</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        <Field label="Full Name *"><input style={input} value={f.full_name} onChange={set('full_name')} /></Field>
        <Field label="Father's Name"><input style={input} value={f.father_name} onChange={set('father_name')} /></Field>
        <Field label="Mother's Name"><input style={input} value={f.mother_name} onChange={set('mother_name')} /></Field>
        <Field label="Date of Birth"><input type="date" style={input} value={f.date_of_birth} onChange={set('date_of_birth')} /></Field>
        <Field label="Gender">
          <select style={input} value={f.gender} onChange={set('gender')}><option value="">—</option><option value="male">Male</option><option value="female">Female</option><option value="other">Other</option></select>
        </Field>
        <Field label="Marital Status">
          <select style={input} value={f.marital_status} onChange={set('marital_status')}><option value="">—</option><option value="single">Single</option><option value="married">Married</option><option value="other">Other</option></select>
        </Field>
        <Field label="Phone"><input style={input} value={f.phone_number} onChange={set('phone_number')} /></Field>
        <Field label="Email"><input style={input} value={f.email_id} onChange={set('email_id')} /></Field>
        <Field label="Emergency Contact"><input style={input} value={f.emergency_number} onChange={set('emergency_number')} /></Field>
        <Field label="Aadhaar Number"><input style={input} value={f.aadhaar_number} onChange={set('aadhaar_number')} /></Field>
        <Field label="Category"><input style={input} value={f.category} onChange={set('category')} placeholder="free text" /></Field>
        <div />
        <div style={{ gridColumn: '1/-1' }}><Field label="Correspondence Address"><input style={input} value={f.correspondence_address} onChange={set('correspondence_address')} /></Field></div>
        <div style={{ gridColumn: '1/-1' }}><Field label="Permanent Address"><input style={input} value={f.permanent_address} onChange={set('permanent_address')} /></Field></div>
        <div style={{ gridColumn: '1/-1' }}><Field label="Education"><input style={input} value={f.education} onChange={set('education')} /></Field></div>
        <div style={{ gridColumn: '1/-1' }}><Field label="Experience"><input style={input} value={f.experience} onChange={set('experience')} /></Field></div>
      </div>

      <SectionTitle>Employment</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        <Field label="Department">
          <select style={input} value={f.department_id} onChange={(e) => { set('department_id')(e); setF(p => ({ ...p, designation_id: '' })); }}>
            <option value="">— Select —</option>{depts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </Field>
        <Field label="Designation">
          <select style={input} value={f.designation_id} onChange={set('designation_id')} disabled={!f.department_id}>
            <option value="">— Select —</option>{desigs.map(d => <option key={d.id} value={d.id}>{d.title}</option>)}
          </select>
        </Field>
        <Field label="Pay Type">
          <select style={input} value={f.employee_type} onChange={set('employee_type')}>
            <option value="monthly">Monthly</option><option value="daily">Daily Wage</option><option value="piece_rate">Piece Rate</option><option value="contractor">Contractor</option>
          </select>
        </Field>
        <Field label="Salary / Rate"><input type="number" style={input} value={f.salary} onChange={set('salary')} placeholder="optional" /></Field>
        <Field label="Date of Joining"><input type="date" style={input} value={f.date_of_joining} onChange={set('date_of_joining')} /></Field>
        <div />
        <Field label="Shift Start"><input type="time" style={input} value={f.shift_start} onChange={set('shift_start')} /></Field>
        <Field label="Shift End"><input type="time" style={input} value={f.shift_end} onChange={set('shift_end')} /></Field>
      </div>

      <SectionTitle>Statutory</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        <Field label="ESI No"><input style={input} value={f.esi_no} onChange={set('esi_no')} /></Field>
        <Field label="PF No"><input style={input} value={f.pf_no} onChange={set('pf_no')} /></Field>
        <Field label="UAN"><input style={input} value={f.uan} onChange={set('uan')} /></Field>
      </div>

      <SectionTitle>Family</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        <Field label={spouseLabel}><input style={input} value={f.spouse_name} onChange={set('spouse_name')} /></Field>
        <Field label="Spouse DOB"><input type="date" style={input} value={f.spouse_dob} onChange={set('spouse_dob')} /></Field>
        <Field label="Emergency Contact Relation"><input style={input} value={f.emergency_contact_relation} onChange={set('emergency_contact_relation')} placeholder="e.g. Father, Spouse" /></Field>
      </div>
      <div style={{ marginTop: 12 }}>
        <div style={{ fontSize: 10, color: '#888', fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>Children / Dependents</div>
        {children.map((c, idx) => (
          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <select style={{ ...input, width: 110 }} value={c.relation} onChange={e => setChildren(arr => arr.map((x, i) => i === idx ? { ...x, relation: e.target.value } : x))}>
              <option value="child">Child</option><option value="other">Other</option>
            </select>
            <input style={{ ...input, flex: 1 }} placeholder="Name" value={c.name} onChange={e => setChildren(arr => arr.map((x, i) => i === idx ? { ...x, name: e.target.value } : x))} />
            <input type="date" style={{ ...input, width: 150 }} value={c.dob} onChange={e => setChildren(arr => arr.map((x, i) => i === idx ? { ...x, dob: e.target.value } : x))} />
            <Btn size="sm" variant="ghost" onClick={() => setChildren(arr => arr.filter((_, i) => i !== idx))}>Remove</Btn>
          </div>
        ))}
        <Btn size="sm" variant="soft" onClick={() => setChildren(arr => [...arr, { relation: 'child', name: '', dob: '' }])}>+ Add Child</Btn>
      </div>

      <SectionTitle>Documents</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        {DOC_SLOTS.map(d => (
          <Field key={d.key} label={d.label}>
            <input type="file" accept="image/*,application/pdf" style={{ ...input, padding: 6 }} onChange={e => setFiles(p => ({ ...p, [d.key]: e.target.files[0] }))} />
          </Field>
        ))}
      </div>

      {err && <div style={{ color: HRC.red, fontSize: 12, marginTop: 12 }}>{err}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn onClick={submit} disabled={saving}>{saving ? 'Creating Employee…' : '✓ Hire & Create Employee'}</Btn>
      </div>
    </Modal>
  );
}

/* ── helpers ── */
function ReadField({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: '#888', fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13, color: HRC.text, textTransform: label === 'Marital Status' || label === 'Source' ? 'capitalize' : 'none' }}>{value || '—'}</div>
    </div>
  );
}
// Keep only requisition form fields when editing (drop server-only columns).
function clean(rq) {
  const keys = ['job_title', 'roles_responsibility', 'total_vacancy', 'urgency', 'expected_hiring_date',
    'gender_preferred', 'salary_range', 'marital_status_pref', 'experience_required', 'qualification',
    'special_skills', 'age_range', 'job_timing', 'working_tools', 'conveyance_required', 'others_notes', 'form_filler'];
  const out = {};
  keys.forEach(k => { if (rq[k] != null) out[k] = rq[k]; });
  if (out.expected_hiring_date) out.expected_hiring_date = String(out.expected_hiring_date).slice(0, 10);
  return out;
}
const dval = (v) => (v ? String(v).slice(0, 10) : '');

// ═══════════════════════════════════════════════════════════════════════════
// EVALUATION RUBRIC + SCORING + WRITTEN GRADING (mig 112)
// ═══════════════════════════════════════════════════════════════════════════

// Rubric editor — weights must total 100 (Save disabled otherwise) + Apply template.
function RubricEditor({ requisition, onClose }) {
  const [rows, setRows] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const load = useCallback(() => { api.get(`/hr/recruitment/requisitions/${requisition.id}/criteria`).then(r => setRows((r.data.data || []).map(c => ({ ...c })))).catch(() => setRows([])); }, [requisition.id]);
  useEffect(() => { load(); }, [load]);
  const total = (rows || []).reduce((s, r) => s + (parseInt(r.weight_pct, 10) || 0), 0);
  const applyTemplate = (replace) => {
    setMsg('');
    api.post(`/hr/recruitment/requisitions/${requisition.id}/apply-template`, { replace: !!replace })
      .then(() => { setMsg('Template applied — rubric + questions copied.'); load(); })
      .catch(e => {
        if (e.response?.status === 409 && e.response?.data?.needsConfirm) { if (window.confirm(e.response.data.error + '\n\nContinue?')) applyTemplate(true); }
        else setMsg(e.response?.data?.error || 'Failed');
      });
  };
  const setRow = (i, k, v) => setRows(rows.map((r, j) => j === i ? { ...r, [k]: v } : r));
  const save = () => { setSaving(true); setMsg(''); api.put(`/hr/recruitment/requisitions/${requisition.id}/criteria`, { criteria: rows }).then(() => setMsg('Saved.')).catch(e => setMsg(e.response?.data?.error || 'Failed')).finally(() => setSaving(false)); };
  return (
    <Modal title={`Scoring Rubric — ${requisition.job_title}`} onClose={onClose} width={640}>
      {rows === null ? <Loading /> : (
        <div>
          {rows.length === 0 && <div style={{ background: '#EEEDF8', padding: 14, borderRadius: 8, marginBottom: 14, fontSize: 13 }}>No rubric yet. <Btn size="sm" onClick={applyTemplate}>Apply Accountant template</Btn> — copies the 6 criteria + 9 questions onto this requisition.</div>}
          {rows.map((r, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
              <input style={{ ...input, flex: 1 }} value={r.criterion} onChange={e => setRow(i, 'criterion', e.target.value)} placeholder="Criterion" />
              <input style={{ ...input, width: 68 }} type="number" value={r.weight_pct} onChange={e => setRow(i, 'weight_pct', e.target.value)} />
              <span style={{ fontSize: 12 }}>%</span>
              <label style={{ fontSize: 11, display: 'flex', gap: 4, alignItems: 'center', whiteSpace: 'nowrap' }} title="Auto-filled from the test score (read-only for humans)"><input type="checkbox" checked={!!r.is_auto} onChange={e => setRow(i, 'is_auto', e.target.checked)} />test</label>
              <span onClick={() => setRows(rows.filter((_, j) => j !== i))} style={{ cursor: 'pointer', color: HRC.red, fontWeight: 700 }}>×</span>
            </div>
          ))}
          <Btn size="sm" variant="ghost" onClick={() => setRows([...(rows || []), { criterion: '', weight_pct: 0, is_auto: false, sort_order: rows.length }])}>+ Add criterion</Btn>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, borderTop: '1px solid #eee', paddingTop: 12 }}>
            <span style={{ fontWeight: 700, color: total === 100 ? HRC.green : HRC.red }}>Total: {total}% {total === 100 ? '✓' : '— must be 100'}</span>
            <div>{msg && <span style={{ fontSize: 12, marginRight: 10, color: HRC.muted }}>{msg}</span>}<Btn onClick={save} disabled={total !== 100 || saving}>{saving ? 'Saving…' : 'Save rubric'}</Btn></div>
          </div>
        </div>
      )}
    </Modal>
  );
}

// Side-by-side comparison of a requisition's candidates against the rubric.
function CompareMatrix({ requisition, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/hr/recruitment/requisitions/${requisition.id}/compare`).then(r => setD(r.data.data)).catch(() => setD({ criteria: [], candidates: [] })); }, [requisition.id]);
  return (
    <Modal title={`Compare Candidates — ${requisition.job_title}`} onClose={onClose} width={880}>
      {!d ? <Loading /> : d.candidates.length === 0 ? <Empty>No candidates to compare yet.</Empty> : (
        <div style={{ overflowX: 'auto' }}>
          <Table>
            <thead><tr><Th>Candidate</Th>{d.criteria.map(c => <Th key={c.id} style={{ textAlign: 'center' }}>{c.criterion}<div style={{ fontWeight: 400, color: HRC.muted, fontSize: 10 }}>{c.weight_pct}%{c.is_auto ? ' · test' : ''}</div></Th>)}<Th style={{ textAlign: 'right' }}>Weighted</Th></tr></thead>
            <tbody>{d.candidates.map((c, idx) => (
              <tr key={c.candidate_id} style={{ background: idx === 0 ? '#F0FDF4' : 'transparent' }}>
                <Td style={{ fontWeight: 600 }}>{idx === 0 ? '🏆 ' : ''}{c.candidate_name}</Td>
                {d.criteria.map(cr => <Td key={cr.id} style={{ textAlign: 'center' }}>{c.cells[cr.id] != null ? c.cells[cr.id] : '—'}</Td>)}
                <Td style={{ textAlign: 'right', fontWeight: 800, color: HRC.navy }}>{c.weighted_total}</Td>
              </tr>
            ))}</tbody>
          </Table>
        </div>
      )}
    </Modal>
  );
}

// Candidate scorecard (inside the candidate detail): send test + score against rubric.
function CandidateScorecard({ candidateId }) {
  const [d, setD] = useState(null);
  const [msg, setMsg] = useState('');
  const [dirty, setDirty] = useState({});
  const load = useCallback(() => { api.get(`/hr/recruitment/candidates/${candidateId}/scorecard`).then(r => setD(r.data.data)).catch(() => setD(null)); }, [candidateId]);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;
  if (!d.requisition_id) return null;   // the VacancyPicker in the card shows the "not linked" state
  // Proctored: HR launches the test full-screen on this machine — no link is sent home.
  const startTest = () => { setMsg(''); api.post(`/hr/recruitment/candidates/${candidateId}/send-test`).then(r => window.open(r.data.data.url, '_blank', 'noopener')).catch(e => setMsg(e.response?.data?.error || 'Failed')); };
  const saveScores = () => {
    const scores = Object.entries(dirty).map(([criterion_id, score]) => ({ criterion_id, score: Number(score) }));
    if (!scores.length) return;
    api.put(`/hr/recruitment/candidates/${candidateId}/scores`, { scores }).then(() => { setDirty({}); setMsg('Scores saved.'); load(); }).catch(e => setMsg(e.response?.data?.error || 'Failed'));
  };
  const liveTotal = d.criteria.reduce((s, c) => { const v = c.is_auto ? c.score : (dirty[c.id] != null ? Number(dirty[c.id]) : c.score); return s + (v != null && v !== '' ? (v / 5) * c.weight_pct : 0); }, 0);
  return (
    <div style={{ marginTop: 16 }}>
      <SectionTitle right={<Btn size="sm" onClick={startTest} title="Opens the test full-screen on this machine — the candidate takes it here, in front of you">▶ Start Test</Btn>}>Evaluation Scorecard</SectionTitle>
      {d.criteria.length === 0 ? <div style={{ fontSize: 12, color: HRC.muted }}>No rubric on this requisition yet — set it in Job Openings → Rubric.</div> : (
        <>
          {d.criteria.map(c => (
            <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <span style={{ flex: 1, fontSize: 13 }}>{c.criterion} <span style={{ color: HRC.muted, fontSize: 11 }}>· {c.weight_pct}%</span></span>
              {c.is_auto
                ? <span style={{ fontSize: 12, fontWeight: 700, color: HRC.navy }}>{c.score != null ? `${c.score}/5` : 'test pending'}{c.test_pct != null ? ` · ${c.test_pct}%` : ''} <span title="Auto from the test — read-only">🔒</span></span>
                : <input type="number" min={0} max={5} step={0.5} style={{ ...input, width: 70 }} value={dirty[c.id] != null ? dirty[c.id] : (c.score != null ? c.score : '')} onChange={e => setDirty({ ...dirty, [c.id]: e.target.value })} />}
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, borderTop: '1px solid #eee', paddingTop: 8 }}>
            <span style={{ fontWeight: 800, color: HRC.navy }}>Weighted total: {Math.round(liveTotal * 10) / 10} / 100</span>
            <div>{msg && <span style={{ fontSize: 11, marginRight: 8, color: HRC.muted }}>{msg}</span>}<Btn size="sm" onClick={saveScores} disabled={!Object.keys(dirty).length}>Save scores</Btn></div>
          </div>
        </>
      )}
    </div>
  );
}

// HR queue of submitted candidate tests awaiting written grading.
function GradingQueue() {
  const [rows, setRows] = useState(null);
  const [grade, setGrade] = useState(null);
  const load = useCallback(() => { api.get('/assessment/candidate-queue').then(r => setRows(r.data.data)).catch(() => setRows([])); }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <Card>
      <SectionTitle>Written Answers to Grade</SectionTitle>
      {rows === null ? <Loading /> : rows.length === 0 ? <Empty>No submitted tests awaiting grading.</Empty> : (
        <Table>
          <thead><tr><Th>Candidate</Th><Th>Role</Th><Th>MCQ (auto)</Th><Th>Submitted</Th><Th style={{ textAlign: 'right' }}></Th></tr></thead>
          <tbody>{rows.map(r => (
            <tr key={r.id}><Td style={{ fontWeight: 600 }}>{r.candidate_name}</Td><Td>{r.job_title || '—'}</Td><Td>{r.score}/{r.total}</Td><Td>{fmtDate(r.created_at)}</Td>
              <Td style={{ textAlign: 'right' }}><Btn size="sm" onClick={() => setGrade(r.id)}>Grade</Btn></Td></tr>
          ))}</tbody>
        </Table>
      )}
      {grade && <GradeAttempt attemptId={grade} onClose={() => setGrade(null)} onGraded={() => { setGrade(null); load(); }} />}
    </Card>
  );
}

// Grade one attempt's written answers → combined % feeds the Technical criterion.
function GradeAttempt({ attemptId, onClose, onGraded }) {
  const [d, setD] = useState(null);
  const [marks, setMarks] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get(`/assessment/candidate-attempt/${attemptId}`).then(r => setD(r.data.data)).catch(() => setD(null)); }, [attemptId]);
  const submit = () => {
    setSaving(true);
    api.post(`/assessment/candidate-attempt/${attemptId}/grade`, { marks: Object.fromEntries(Object.entries(marks).map(([k, v]) => [k, Number(v)])) })
      .then(r => { alert(`Graded — combined ${r.data.data.combined_pct}%. It now feeds the candidate's Technical score.`); onGraded(); })
      .catch(e => alert(e.response?.data?.error || 'Failed')).finally(() => setSaving(false));
  };
  return (
    <Modal title="Grade Written Answers" onClose={onClose} width={720}>
      {!d ? <Loading /> : (
        <div>
          <div style={{ fontSize: 12, color: HRC.muted, marginBottom: 12 }}>MCQ (auto-scored): <b>{d.mcq.score}/{d.mcq.total}</b></div>
          {d.written.map((w, i) => {
            // A written question resolves to a live quiz_questions row ONLY if it still
            // exists. If the question was hard-deleted after the candidate answered
            // (re-applying a requisition's questionnaire runs DELETE ... WHERE
            // requisition_id), the fetch's join returns nothing and w.question / w.marks
            // arrive undefined. Show that plainly ("not set" / "removed") rather than a
            // blank "( marks)" and an empty denominator — but do NOT block grading: a
            // grader who can read the answer can still award sensibly.
            const hasMarks = Number.isFinite(Number(w.marks)) && Number(w.marks) > 0;
            const orphaned = w.question == null;
            return (
            <div key={w.id} style={{ marginBottom: 16, borderBottom: '1px solid #eee', paddingBottom: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>
                Q{i + 1}. {orphaned
                  ? <span style={{ color: '#C2410C', fontWeight: 600 }}>⚠ Question no longer in the set — text unavailable (it was changed after the candidate answered)</span>
                  : w.question}
                {' '}<span style={{ color: hasMarks ? HRC.muted : '#C2410C', fontWeight: 400 }}>({hasMarks ? `${w.marks} marks` : 'marks not set'})</span>
              </div>
              <div style={{ background: '#F7F6F3', padding: 10, borderRadius: 6, fontSize: 13, whiteSpace: 'pre-wrap', marginBottom: 8 }}>{w.answer || <i style={{ color: HRC.muted }}>No answer given.</i>}</div>
              {w.model_answer && <div style={{ fontSize: 12, color: '#166534', background: '#EAF7EE', border: '1px solid #cde6d5', borderRadius: 6, padding: '6px 10px', marginBottom: 8 }}><b>Reference answer:</b> {w.model_answer}</div>}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}><span style={{ fontSize: 12 }}>Award:</span><input type="number" min={0} max={hasMarks ? w.marks : undefined} style={{ ...input, width: 80 }} value={marks[w.id] || ''} onChange={e => setMarks({ ...marks, [w.id]: e.target.value })} /><span style={{ fontSize: 12, color: hasMarks ? HRC.muted : '#C2410C' }}>/ {hasMarks ? w.marks : 'not set'}</span></div>
            </div>
          );
          })}
          <div style={{ textAlign: 'right' }}><Btn onClick={submit} disabled={saving}>{saving ? 'Saving…' : 'Submit grades'}</Btn></div>
        </div>
      )}
    </Modal>
  );
}

// View/EDIT the questions THIS role's candidates are asked. The template is a starting
// point — a fabric checker and a merchant need different questions.
function QuestionsEditor({ requisition, onClose }) {
  const [rows, setRows] = useState(null);
  const [msg, setMsg] = useState('');
  const load = useCallback(() => { api.get(`/hr/recruitment/requisitions/${requisition.id}/questions`).then(r => setRows(r.data.data || [])).catch(() => setRows([])); }, [requisition.id]);
  useEffect(() => { load(); }, [load]);
  const setRow = (i, patch) => setRows(rows.map((r, j) => j === i ? { ...r, ...patch } : r));
  const saveQ = (q) => {
    setMsg('');
    const body = { q_type: q.q_type, difficulty: q.difficulty, question: q.question, options: q.options, answer_index: q.answer_index, marks: q.marks, model_answer: q.model_answer };
    const p = q.id ? api.patch(`/hr/recruitment/questions/${q.id}`, body) : api.post(`/hr/recruitment/requisitions/${requisition.id}/questions`, body);
    p.then(() => { setMsg('Saved.'); load(); }).catch(e => setMsg(e.response?.data?.error || 'Failed'));
  };
  const toggle = (q) => api.patch(`/hr/recruitment/questions/${q.id}`, { is_active: !q.is_active }).then(load).catch(reportError('change this question'));
  const add = (type) => setRows([...(rows || []), type === 'mcq'
    ? { q_type: 'mcq', difficulty: 'medium', question: '', options: ['', '', '', ''], answer_index: 0, is_active: true }
    : { q_type: 'written', difficulty: 'medium', marks: 5, question: '', model_answer: '', is_active: true }]);
  const cnt = (t, d) => (rows || []).filter(q => q.q_type === t && q.difficulty === d && q.is_active !== false).length;
  const applyTemplate = (replace) => {
    setMsg('');
    api.post(`/hr/recruitment/requisitions/${requisition.id}/apply-template`, { replace: !!replace })
      .then(() => { setMsg('Template applied.'); load(); })
      .catch(e => { if (e.response?.status === 409 && e.response?.data?.needsConfirm) { if (window.confirm(e.response.data.error + '\n\nContinue?')) applyTemplate(true); } else setMsg(e.response?.data?.error || 'Failed'); });
  };
  return (
    <Modal title={`Test Questions — ${requisition.job_title}`} onClose={onClose} width={760}>
      {rows === null ? <Loading /> : (
        <div>
          <div style={{ fontSize: 12, color: HRC.muted, marginBottom: 6 }}>What this role's candidates are asked. Edit freely — MCQs are auto-scored, written answers are graded by you. Every question needs a difficulty (the draw is stratified).</div>
          <div style={{ fontSize: 11, color: HRC.muted, marginBottom: 12, background: HRC.violet, borderRadius: 6, padding: '6px 10px' }}>
            MCQ {cnt('mcq', 'easy')}E / {cnt('mcq', 'medium')}M / {cnt('mcq', 'hard')}H &nbsp;·&nbsp; Written {cnt('written', 'easy')}E / {cnt('written', 'medium')}M / {cnt('written', 'hard')}H
          </div>
          {rows.map((q, i) => (
            <div key={q.id || `new${i}`} style={{ border: `1px solid ${q.is_active === false ? '#eee' : HRC.border}`, borderRadius: 8, padding: 12, marginBottom: 10, opacity: q.is_active === false ? 0.55 : 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: q.q_type === 'mcq' ? HRC.navy : '#B45309' }}>{q.q_type === 'mcq' ? 'MCQ · auto-scored' : 'Written · you grade it'}</span>
                  <select value={q.difficulty || ''} onChange={e => setRow(i, { difficulty: e.target.value })} style={{ ...input, width: 'auto', padding: '2px 6px', fontSize: 11 }}>
                    <option value="">difficulty…</option><option value="easy">easy</option><option value="medium">medium</option><option value="hard">hard</option>
                  </select>
                </div>
                {q.id && <span onClick={() => toggle(q)} style={{ cursor: 'pointer', fontSize: 11, color: q.is_active === false ? HRC.green : HRC.red }}>{q.is_active === false ? 'Restore' : 'Remove'}</span>}
              </div>
              <textarea style={{ ...input, minHeight: 44, resize: 'vertical', marginBottom: 6 }} value={q.question || ''} onChange={e => setRow(i, { question: e.target.value })} placeholder="Question" />
              {q.q_type === 'mcq' ? (
                <div>
                  {(q.options || []).map((opt, oi) => (
                    <div key={oi} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4 }}>
                      <input type="radio" checked={q.answer_index === oi} onChange={() => setRow(i, { answer_index: oi })} title="Correct answer" />
                      <input style={{ ...input, flex: 1 }} value={opt} onChange={e => setRow(i, { options: q.options.map((o, j) => j === oi ? e.target.value : o) })} placeholder={`Option ${oi + 1}`} />
                      {(q.options || []).length > 2 && <span onClick={() => setRow(i, { options: q.options.filter((_, j) => j !== oi), answer_index: q.answer_index >= oi ? Math.max(0, q.answer_index - 1) : q.answer_index })} style={{ cursor: 'pointer', color: HRC.red }}>×</span>}
                    </div>
                  ))}
                  {(q.options || []).length < 4 && <Btn size="sm" variant="ghost" onClick={() => setRow(i, { options: [...(q.options || []), ''] })}>+ option</Btn>}
                  <div style={{ fontSize: 10, color: HRC.muted, marginTop: 3 }}>● = the correct answer</div>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <div style={{ width: 90 }}><Field label="Marks"><input type="number" min={1} style={input} value={q.marks || ''} onChange={e => setRow(i, { marks: e.target.value })} /></Field></div>
                  <div style={{ flex: 1 }}><Field label="Model answer (grading guide — candidate never sees it)"><textarea style={{ ...input, minHeight: 44, resize: 'vertical' }} value={q.model_answer || ''} onChange={e => setRow(i, { model_answer: e.target.value })} /></Field></div>
                </div>
              )}
              <div style={{ textAlign: 'right', marginTop: 6 }}><Btn size="sm" onClick={() => saveQ(q)}>{q.id ? 'Save' : 'Add'}</Btn></div>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
            <Btn size="sm" variant="soft" onClick={() => add('mcq')}>+ MCQ</Btn>
            <Btn size="sm" variant="soft" onClick={() => add('written')}>+ Written</Btn>
            <Btn size="sm" variant="ghost" onClick={() => applyTemplate()} title="Replace this bank with the shared Accountant template">↻ Apply template</Btn>
            {msg && <span style={{ fontSize: 11, color: HRC.muted, marginLeft: 'auto' }}>{msg}</span>}
          </div>
        </div>
      )}
    </Modal>
  );
}

// Link a candidate to a vacancy — the gate for Start Test, rubric and scorecard. "Other"
// is a DEAD END, not a bypass: no vacancy → no test → no score, and the card says so plainly
// (so nobody parks candidates in "Other" to skip raising a requisition).
function VacancyPicker({ candidate, requisitions, onChanged }) {
  const [saving, setSaving] = useState(false);
  const link = (reqId) => { setSaving(true); api.patch(`/hr/recruitment/candidates/${candidate.id}`, { requisition_id: reqId || null }).then(() => onChanged()).catch(reportError('link this vacancy')).finally(() => setSaving(false)); };
  const linked = !!candidate.requisition_id;
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ fontSize: 10, fontWeight: 700, color: HRC.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>Vacancy</label>
      <select value={candidate.requisition_id || ''} disabled={saving} onChange={e => link(e.target.value)} style={{ ...input, marginTop: 4 }}>
        {(requisitions || []).map(r => <option key={r.id} value={r.id}>{r.job_title} · {r.total_vacancy} vacanc{r.total_vacancy > 1 ? 'ies' : 'y'}</option>)}
        <option value="">Other / speculative (not a vacancy)</option>
      </select>
      {!linked && (
        <div style={{ marginTop: 8 }}>
          <input placeholder="Position (free text, e.g. Accounts Manager)" defaultValue={candidate.designation_applied || ''}
            onBlur={e => { if (e.target.value !== (candidate.designation_applied || '')) api.patch(`/hr/recruitment/candidates/${candidate.id}`, { designation_applied: e.target.value }).then(onChanged).catch(reportError('save the position')); }}
            style={{ ...input, marginBottom: 8 }} />
          <div style={{ background: '#FCEBEB', color: '#A32D2D', borderRadius: 6, padding: '9px 12px', fontSize: 12.5 }}>
            <b>Not linked to a vacancy — cannot be tested or scored.</b>
            <div style={{ color: '#7a3b3b', marginTop: 2 }}>Pick a vacancy above to enable Start Test, the rubric and the scorecard.</div>
          </div>
        </div>
      )}
    </div>
  );
}
